import React from 'react';
import styled from 'styled-components';
import { formatDate } from '../utils/dateFormat';

const ResumeContainer = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100%;
  font-family: ${props => props.styles?.fontFamily || 'Roboto, sans-serif'};
`;

const Sidebar = styled.div`
  background: ${props => props.styles?.primaryColor || '#2196f3'};
  color: white;
  padding: 2rem;
`;

const MainContent = styled.div`
  padding: 2rem;
  background: white;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 60px;
  background: white;
  margin: 0 auto 1.5rem;
`;

const ContactInfo = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 13px;
    margin: 0.5rem 0;
  }
`;

const SkillBar = styled.div`
  margin-bottom: 0.8rem;
  
  .skill-name {
    font-size: 13px;
    margin-bottom: 0.3rem;
  }
  
  .skill-level {
    height: 4px;
    background: rgba(255,255,255,0.2);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: ${props => props.level || '0%'};
      background: white;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  h2 {
    font-size: 20px;
    color: ${props => props.styles?.primaryColor || '#2196f3'};
    margin-bottom: 1rem;
    position: relative;
    padding-bottom: 0.5rem;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 40px;
      height: 3px;
      background: ${props => props.styles?.primaryColor || '#2196f3'};
    }
  }
`;

const ExperienceItem = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  padding-left: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 8px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.styles?.primaryColor || '#2196f3'};
  }
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    bottom: -24px;
    width: 1px;
    background: ${props => props.styles?.primaryColor || '#2196f3'};
  }
  
  &:last-child::after {
    display: none;
  }
  
  .description {
    margin-top: 0.5rem;
    
    ul, ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }
    
    p {
      margin: 0.5rem 0;
    }
    
    li {
      margin: 0.25rem 0;
    }
    
    strong {
      font-weight: 600;
    }
    
    em {
      font-style: italic;
    }
  }
`;

const EducationItem = styled.div`
  margin-bottom: 1.5rem;
  
  .school-info {
    margin-bottom: 0.5rem;
  }
  
  .courses {
    margin-top: 0.5rem;
    font-size: 0.9em;
    color: #666;
  }
  
  .gpa {
    color: #666;
    font-size: 0.9em;
  }
`;

const ModernTemplate = ({ data, styles }) => {
  const { personalInfo, education, experience, skills, certificates, technicalSkills, selfEvaluation } = data;

  const calculateSkillLevel = (skill) => {
    // 这里可以根据实际需求设置技能水平
    return Math.floor(Math.random() * 40 + 60) + '%';
  };

  return (
    <ResumeContainer styles={styles}>
      <Sidebar styles={styles}>
        <Avatar>
          {personalInfo?.profileImage && (
            <img
              src={personalInfo.profileImage}
              alt="Avatar"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          )}
        </Avatar> 
        <ContactInfo>
          <h3>联系方式</h3>
          <p>{personalInfo?.email}</p>
          <p>{personalInfo?.phone}</p>
          <p>{personalInfo?.address}</p>
        </ContactInfo>

        <ContactInfo>
          <h3>求职信息</h3>
          {personalInfo?.workYears && (
            <p>工作年限: {personalInfo.workYears}年</p>
          )}
          {personalInfo?.jobIntention && (
            <p>求职意向: {personalInfo.jobIntention}</p>
          )}
          {personalInfo?.targetCity && (
            <p>意向城市: {personalInfo.targetCity}</p>
          )}
          {personalInfo?.expectedSalary && (
            <p>期望薪资: {personalInfo.expectedSalary}</p>
          )}
          {personalInfo?.availableDate && (
            <p>入职时间: {formatDate(personalInfo.availableDate)}</p>
          )}
        </ContactInfo>
        
        <ContactInfo>
          <h3>技能特长</h3>
          <div
            dangerouslySetInnerHTML={{
              __html: Array.isArray(skills) ? skills.join('<br/>') : (skills || '')
            }}
          />
        </ContactInfo>
      </Sidebar>

      <MainContent>
        <Section styles={styles}>
          <h1>{personalInfo?.name}</h1>
          {selfEvaluation && (
            <div className="self-evaluation">
              <h2>自我评价</h2>
              <p>{selfEvaluation}</p>
            </div>
          )}
        </Section>

        {/* 技术技能部分 */}
        {technicalSkills?.length > 0 && (
          <Section styles={styles}>
            <h2>技术技能</h2>
            {technicalSkills.map((category, index) => (
              <div key={index} className="skill-category">
                <h3>{category.category}</h3>
                <div className="skill-items">
                  {category.items.map((item, i) => (
                    <div key={i} className="skill-item">
                      <span>{item.name}</span>
                      <div className="skill-level-indicator">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <span
                            key={j}
                            className={j < item.level ? 'filled' : ''}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* 工作经验部分 */}
        <Section styles={styles}>
          <h2>工作经验</h2>
          {experience?.map((exp, index) => (
            <ExperienceItem key={index} styles={styles}>
              <h3>{exp.position}</h3>
              <p>{exp.company} | {exp.location}</p>
              <p>{formatDate(exp.startDate)} - {formatDate(exp.endDate)}</p>
              <div 
                className="description"
                dangerouslySetInnerHTML={{ 
                  __html: exp.description?.replace(/\n/g, '<br/>') || '' 
                }}
              />
            </ExperienceItem>
          ))}
        </Section>

        {/* 教育背景部分 */}
        <Section styles={styles}>
          <h2>教育背景</h2>
          {education?.map((edu, index) => (
            <EducationItem key={index}>
              <div className="school-info">
                <h3>{edu.school}</h3>
                <p>{edu.degree} · {edu.field}</p>
                <p>{formatDate(edu.startDate)} - {formatDate(edu.endDate)}</p>
              </div>
              {edu.showGpa && edu.gpa && (
                <p className="gpa">专业成绩: {edu.gpa}</p>
              )}
              {edu.showCourses && edu.courses?.length > 0 && (
                <div className="courses">
                  <p>主修课程:</p>
                  <p>{edu.courses.join(' · ')}</p>
                </div>
              )}
            </EducationItem>
          ))}
        </Section>

        {/* 证书部分 */}
        {certificates?.length > 0 && (
          <Section styles={styles}>
            <h2>荣誉证书</h2>
            <div className="certificates-grid">
              {certificates.map((cert, index) => (
                <div key={index} className="certificate-item">
                  <h3>{cert.name}</h3>
                  <p className="issuer">{cert.issuer}</p>
                  <p className="date">{formatDate(cert.issueDate)}</p>
                  {cert.description && (
                    <p className="description">{cert.description}</p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}
      </MainContent>
    </ResumeContainer>
  );
};

export default ModernTemplate; 